import { PayloadAction } from '@reduxjs/toolkit';

import { LOCAL_TABLE_STORAGE_KEYS } from 'app/modules/localTableSettings/constants';
import {
  ColumnMetadata,
  LocalColumnConfig,
  LocalTableConfigState,
  LocalTableConfigType,
} from 'app/modules/localTableSettings/models';
import { buildReconciledColumns } from 'app/modules/localTableSettings/utils';
import { RULE_ADMIN_INFO_MAP } from 'app/modules/rulesAdmin/constants';
import { u21CreateSlice } from 'app/shared/thunk/u21CreateSlice';

const readVisibleKeysFromStorage = (
  type: LocalTableConfigType,
): string[] | undefined => {
  try {
    const raw = localStorage.getItem(LOCAL_TABLE_STORAGE_KEYS[type]);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
};

const buildInitialLocalTableConfigState = (): LocalTableConfigState => {
  const state: LocalTableConfigState = {};
  Object.values(RULE_ADMIN_INFO_MAP).forEach((info) => {
    const columns: ColumnMetadata[] = info.defaultColumns.map((i) => ({
      key: i.id,
      label: typeof i.Header === 'string' ? i.Header : i.id,
    }));
    const defaultVisibleKeys = info.defaultColumns.map((i) => i.id);
    const visibleKeys =
      readVisibleKeysFromStorage(info.localTableConfigType) ??
      defaultVisibleKeys;
    state[info.localTableConfigType] = buildReconciledColumns(
      columns,
      visibleKeys,
    );
  });
  return state;
};

export const LOCAL_TABLE_SETTINGS_SLICE_NAME = 'localTableSettings';

export const localTableSettingsSlice = u21CreateSlice({
  name: LOCAL_TABLE_SETTINGS_SLICE_NAME,
  initialState: buildInitialLocalTableConfigState(),
  reducers: {
    syncLocalTableConfig: (
      draft,
      action: PayloadAction<{
        type: LocalTableConfigType;
        columns: ColumnMetadata[];
        defaultVisibleKeys: string[];
      }>,
    ) => {
      const { type, columns, defaultVisibleKeys } = action.payload;
      const existing = draft[type];
      // `existing` is truthy-but-empty when a prior sync ran before `columns`
      // was available. In that case, fall back to storage/defaults so we seed
      // correctly once real columns arrive.
      const visibleKeys =
        existing && existing.length > 0
          ? existing.filter((c) => !c.hidden).map((c) => c.key)
          : (readVisibleKeysFromStorage(type) ?? defaultVisibleKeys);
      draft[type] = buildReconciledColumns(columns, visibleKeys);
    },
    setLocalTableConfig: (
      draft,
      action: PayloadAction<{
        type: LocalTableConfigType;
        columns: LocalColumnConfig[];
      }>,
    ) => {
      draft[action.payload.type] = action.payload.columns;
    },
  },
});

export const { syncLocalTableConfig, setLocalTableConfig } =
  localTableSettingsSlice.actions;

export default localTableSettingsSlice.reducer;
